import { GetUsuarios } from '@/modules/system/usuario/usuario.service';
import { ReadUsuarioDto } from '@/api.types';
import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { ListarHistoricoLogins, UsuarioLoginDto } from './sessoes.service';

type OpcaoUsuario = { value: number; label: string };

type ModalHistoricoSessoesUsuarioProps = {
  show: boolean;
  onHide: () => void;
};

function formatarData(data?: string | null) {
  if (!data) return '-';
  return new Date(data).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function tituloIp(sessao: UsuarioLoginDto) {
  const partes = [sessao.ipOrigem, sessao.userAgent].filter(Boolean);
  return partes.length > 0 ? partes.join('\n') : undefined;
}

export default function ModalHistoricoSessoesUsuario({ show, onHide }: ModalHistoricoSessoesUsuarioProps) {
  const [listaUsuarios, setListaUsuarios] = useState<ReadUsuarioDto[]>([]);
  const [usuarioId, setUsuarioId] = useState<number | null>(null);
  const [sessoes, setSessoes] = useState<UsuarioLoginDto[]>([]);
  const [carregandoUsuarios, setCarregandoUsuarios] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [pesquisou, setPesquisou] = useState(false);

  useEffect(() => {
    if (!show) return;
    setCarregandoUsuarios(true);
    GetUsuarios({ skip: 0, take: 1000 })
      .then((result) => {
        setListaUsuarios(result[0].sort((a, b) => (a.username || '').localeCompare(b.username || '', 'pt-BR')));
      })
      .catch((error) => {
        console.warn(error);
        toast.error('Falha ao carregar usuários.');
      })
      .finally(() => setCarregandoUsuarios(false));
  }, [show]);

  useEffect(() => {
    if (!show) {
      setUsuarioId(null);
      setSessoes([]);
      setPesquisou(false);
      setBuscando(false);
    }
  }, [show]);

  const opcoesUsuario: OpcaoUsuario[] = useMemo(
    () =>
      listaUsuarios.map((usuario) => ({
        value: usuario.id!,
        label: usuario.pessoa?.nome
          ? `${usuario.username} — ${usuario.pessoa.nome}`
          : usuario.username || String(usuario.id),
      })),
    [listaUsuarios],
  );

  const usuarioSelecionado = opcoesUsuario.find((opcao) => opcao.value === usuarioId) ?? null;

  const handlePesquisar = async () => {
    if (!usuarioId) {
      toast.warning('Selecione um usuário.');
      return;
    }

    try {
      setBuscando(true);
      setPesquisou(true);
      const dados = await ListarHistoricoLogins(usuarioId, 50);
      setSessoes(dados);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || 'Falha ao buscar histórico de sessões.');
      setSessoes([]);
    } finally {
      setBuscando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Histórico de sessões</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-3">
          <div>
            <label htmlFor="historico-sessoes-usuario" className="form-label">
              Usuário
            </label>
            <Select
              inputId="historico-sessoes-usuario"
              classNamePrefix="react-select"
              options={opcoesUsuario}
              value={usuarioSelecionado}
              placeholder={carregandoUsuarios ? 'Carregando…' : 'Selecione um usuário…'}
              isClearable
              isSearchable
              isDisabled={carregandoUsuarios || buscando}
              noOptionsMessage={() => 'Nenhum usuário encontrado'}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              onChange={(opcao) => setUsuarioId(opcao?.value ?? null)}
            />
          </div>

          {buscando ? (
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Buscando…</span>
              </div>
            </div>
          ) : pesquisou && sessoes.length === 0 ? (
            <p className="text-muted mb-0">Nenhuma sessão encontrada para este usuário.</p>
          ) : sessoes.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th>IP</th>
                    <th>Login em</th>
                    <th>Último Acesso</th>
                    <th>Logout em</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sessoes.map((sessao) => (
                    <tr key={sessao.id}>
                      <td>
                        <span title={tituloIp(sessao)}>{sessao.ipOrigem || '-'}</span>
                      </td>
                      <td>{formatarData(sessao.logadoEm)}</td>
                      <td>{formatarData(sessao.ultimoAcesso)}</td>
                      <td>{formatarData(sessao.deslogadoEm)}</td>
                      <td>
                        <span className={`badge ${sessao.ativo ? 'bg-success' : 'bg-secondary'}`}>
                          {sessao.ativo ? 'Ativa' : 'Encerrada'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" onClick={onHide}>
          Fechar
        </button>
        <button
          type="button"
          className="btn btn-outline-success"
          onClick={() => void handlePesquisar()}
          disabled={buscando || !usuarioId}
        >
          {buscando ? 'Buscando…' : 'Pesquisar'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
